import React, { useCallback, useMemo, useState } from "react";
import { useSelector } from "react-redux";
import { useCss } from "react-use";
import { Menu, SelectTrigger } from "@activecollab/components";
import moment from "moment";
import PropTypes from "prop-types";
import { getFirstWeekDay, getPersonalizedDateFormat } from "~/store/selectors/selector.configOptions.jsx";
import { formatDateForDateStepper } from "~/utils/date.jsx";
import DateStepperFirstWeekDay from "./DateStepperFirstWeekDay.jsx";
import { defaultStep, getStepDates, getStepVerbose, steps } from "./util.jsx";
import useAcSelectDate from "../Hooks/useAcSelectDate.jsx";

const format = "YYYY-MM-DD";

const StepVerbose = ({ step, onClick }) => {
  const verboseStep = useMemo(() => {
    return getStepVerbose(step);
  }, [step]);

  const handleOnClick = useCallback(() => {
    onClick(step);
  }, [onClick, step]);

  return (
    <li
      onClick={handleOnClick}
      className="tw-capitalize tw-list-none tw-px-4 tw-py-1 hover:tw-bg-primary hover:tw-text-theme-100 tw-cursor-pointer"
    >
      {verboseStep}
    </li>
  );
};

StepVerbose.propTypes = {
  step: PropTypes.oneOf(steps),
  onClick: PropTypes.func,
};

const DateStepperWithRange = ({
  step: selectedStep,
  from: defaultFrom,
  to: defaultTo,
  onChange,
  disabledSteps,
  maxValue,
  rangeClassName,
}) => {
  const [open, setOpen] = useState(false);
  const [step, setStep] = useState(selectedStep ? selectedStep : defaultStep);
  const [dates, setDates] = useState({ from: defaultFrom, to: defaultTo });
  const firstWeekDay = useSelector(getFirstWeekDay);
  const dateFormat = useSelector(getPersonalizedDateFormat);
  const { today } = useAcSelectDate();

  const handleOpen = useCallback(() => {
    setOpen(true);
  }, []);

  const handleClose = useCallback(() => {
    setOpen(false);
  }, []);

  const handleOnClick = useCallback(
    (nextStep) => {
      const nextDates = getStepDates(nextStep, firstWeekDay, dates.from, dates.to);
      setOpen(false);
      setStep(nextStep);
      const formattedDates = {
        from: nextDates.from.format(format),
        to: nextDates.to.format(format),
      };
      setDates(formattedDates);
      if (typeof onChange === "function") {
        onChange({ ...formattedDates, step: nextStep });
      }
    },
    [onChange, firstWeekDay, dates]
  );

  const handleOnChange = useCallback(
    (nextFrom, nextTo) => {
      const dates = {
        from: moment.utc(nextFrom).format(format),
        to: moment.utc(nextTo).format(format),
      };
      setDates(dates);
      if (typeof onChange === "function") {
        onChange({ ...dates, step: selectedStep });
      }
    },
    [onChange, selectedStep]
  );

  const formatDateStepper = useCallback(
    (nextFrom, nextTo) => {
      return formatDateForDateStepper(nextFrom, nextTo, dateFormat, step, firstWeekDay);
    },
    [dateFormat, firstWeekDay, step]
  );

  const verboseStep = useMemo(() => {
    return getStepVerbose(step);
  }, [step]);

  const filteredSteps = useMemo(() => {
    return steps.filter((step) => !disabledSteps.some((value) => value === step));
  }, [disabledSteps]);

  const maxWidth = useCss({
    maxWidth: "124px",
  });

  return (
    <>
      <Menu
        target={<SelectTrigger className={`tw-mr-2 ${maxWidth} ${rangeClassName}`}>{verboseStep}</SelectTrigger>}
        open={open}
        onOpen={handleOpen}
        onClose={handleClose}
        menuClassName="tw-h-26 tw-w-auto tw-mt-1 tw-bg-page-paper-main"
        popperClassName="z-index-10002"
        targetClassName="tw-relative"
        backgroundElementClass="tw-bg-transparent z-index-10001"
      >
        <ul className="tw-p-0 tw-m-0 tw-py-1">
          {filteredSteps.map((value) => {
            return (
              <StepVerbose
                key={value}
                step={value}
                onClick={handleOnClick}
              />
            );
          })}
        </ul>
      </Menu>
      {selectedStep !== "all" ? (
        <div data-testid="date-stepper">
          <DateStepperFirstWeekDay
            step={step}
            from={dates.from}
            to={dates.to}
            onChange={handleOnChange}
            formatCallback={formatDateStepper}
            withDatePicker
            maxValue={maxValue ?? today}
          />
        </div>
      ) : null}
    </>
  );
};

DateStepperWithRange.propTypes = {
  step: PropTypes.oneOf(steps),
  from: PropTypes.string,
  to: PropTypes.string,
  rangeClassName: PropTypes.string,
  onChange: PropTypes.func,
  disabledSteps: PropTypes.arrayOf(PropTypes.oneOf(steps)),
  maxValue: PropTypes.oneOfType([
    PropTypes.instanceOf(Date),
    PropTypes.shape({
      _isAMomentObject: PropTypes.bool.isRequired,
    }),
  ]),
};

DateStepperWithRange.defaultProps = {
  step: defaultStep,
  from: moment().format("YYYY-MM-DD"),
  to: moment().format("YYYY-MM-DD"),
  disabledSteps: [],
};

export default DateStepperWithRange;
